import React, { useEffect, useRef } from 'react';

const ReserveReminderModal = ({ notification, onClose }) => {
  const hasPlayedVoiceRef = useRef(false);

  // Debug logging
  useEffect(() => {
    if (notification) {
      console.log('🔔 ReserveReminderModal rendered with notification:', notification);
      console.log('🔔 Notification keys:', Object.keys(notification));
      console.log('🔔 Reminder type:', notification.reminderType);
      console.log('🔔 Booking data:', notification.booking);
    } else {
      console.log('⚠️ ReserveReminderModal: No notification provided');
    }
  }, [notification]);

  // Play audio file when modal appears
  useEffect(() => {
    if (!notification || hasPlayedVoiceRef.current) return;

    let timeoutId = null;
    let audioElement = null;

    const playAudio = () => {
      try {
        const { reminderType } = notification;
        
        if (!reminderType) {
          console.warn('⚠️ No reminder type found, skipping audio playback');
          return;
        }

        // Determine if user is driver or regular user
        // Check localStorage for user info or check if we're on driver dashboard
        const userStr = localStorage.getItem('uttaapp_user');
        let isDriver = false;
        
        try {
          if (userStr) {
            const user = JSON.parse(userStr);
            isDriver = !!user.driverId;
          }
        } catch (e) {
          console.warn('⚠️ Could not parse user from localStorage');
        }
        
        // Also check current pathname as fallback
        if (!isDriver && window.location.pathname.includes('/driver')) {
          isDriver = true;
        }

        // Map reminder type to audio file
        let audioPath = '';
        
        switch (reminderType) {
          case '5hour':
            audioPath = isDriver ? '/driver/reserve5hours.mp3' : '/user/user reserve5hour.mp3';
            break;
          case '1hour':
            audioPath = isDriver ? '/driver/reserve1hour.mp3' : '/user/user reserve1hour.mp3';
            break;
          case '30min':
            // Note: Driver file has a space in the name "reserve 30min.mp3"
            audioPath = isDriver ? '/driver/reserve 30min.mp3' : '/user/user reserved30min.mp3';
            break;
          case '10min':
            audioPath = isDriver ? '/driver/10minreserve.mp3' : '/user/user reserve10min.mp3';
            break;
          default:
            console.warn(`⚠️ Unknown reminder type: ${reminderType}`);
            return;
        }
        
        // URL encode the path to handle spaces in filenames
        audioPath = encodeURI(audioPath);

        console.log(`🔊 Playing audio for ${isDriver ? 'driver' : 'user'}: ${audioPath}`);
        console.log(`🔊 Reminder type: ${reminderType}`);

        // Create audio element
        audioElement = new Audio(audioPath);
        audioElement.volume = 1.0;
        audioElement.preload = 'auto';

        audioElement.onloadeddata = () => {
          console.log('✅ Audio loaded successfully:', audioPath);
        };

        audioElement.onerror = (err) => {
          console.error('❌ Audio loading/playback error:', err);
          console.error('❌ Audio path:', audioPath);
          console.error('❌ Error details:', audioElement.error);
        };

        audioElement.onended = () => {
          console.log('🔊 Audio playback completed');
          hasPlayedVoiceRef.current = true;
        };

        // Small delay to ensure modal is visible
        timeoutId = setTimeout(() => {
          const playPromise = audioElement.play();
          
          if (playPromise !== undefined) {
            playPromise
              .then(() => {
                console.log('✅ Audio playback started successfully');
                hasPlayedVoiceRef.current = true;
              })
              .catch((error) => {
                console.error('❌ Error playing audio:', error);
                console.error('❌ Audio path:', audioPath);
                // User interaction might be required - try again on user interaction
                console.log('💡 Audio play failed - may require user interaction');
              });
          }
        }, 300);
      } catch (error) {
        console.error('❌ Error in playAudio:', error);
      }
    };

    playAudio();

    // Cleanup: stop audio when component unmounts
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (audioElement) {
        audioElement.pause();
        audioElement.currentTime = 0;
        audioElement = null;
      }
      hasPlayedVoiceRef.current = false;
    };
  }, [notification]);

  if (!notification) {
    console.log('⚠️ ReserveReminderModal: Returning null - no notification');
    return null;
  }

  const { title, message, scheduledTime, scheduledDate, reminderType, booking } = notification;

  // Get reminder type display text
  const getReminderTypeText = (type) => {
    switch (type) {
      case '5hour':
        return '5 Hours';
      case '1hour':
        return '1 Hour';
      case '30min':
        return '30 Minutes';
      case '10min':
        return '10 Minutes';
      default:
        return 'Soon';
    }
  };

  // Format time display
  const displayTime = scheduledTime || (booking?.scheduledStartTime || '');
  const displayDate = scheduledDate || (booking?.scheduledStartDate 
    ? new Date(booking.scheduledStartDate).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      })
    : '');

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[10006] p-4" style={{ zIndex: 10006 }}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-slideUp">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="flex items-center justify-center h-12 w-12 rounded-full bg-blue-100">
              <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">{title || 'UttaApp Reminder'}</h2>
              <p className="text-sm text-gray-500">Reserved Ride Reminder</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="space-y-4">
          {/* Reminder Badge */}
          {reminderType && (
            <div className="inline-flex items-center px-3 py-1 rounded-full bg-blue-100 text-blue-800 text-sm font-medium">
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {getReminderTypeText(reminderType)} Before Scheduled Time
            </div>
          )}

          {/* Message */}
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-gray-700 text-base leading-relaxed">{message}</p>
          </div>

          {/* Booking Details */}
          {booking && (
            <div className="border-t border-gray-200 pt-4 space-y-3">
              <div className="flex items-start space-x-3">
                <svg className="w-5 h-5 text-gray-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-500">Scheduled Time</p>
                  <p className="text-base font-semibold text-gray-900">
                    {displayDate} {displayTime && `at ${displayTime}`}
                  </p>
                </div>
              </div>

              {(booking.pickupLocation || booking.pickupLocationName) && (
                <div className="flex items-start space-x-3">
                  <svg className="w-5 h-5 text-gray-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-500">Pickup Location</p>
                    {/* Priority: pickupLocation (Google Places address from "Location Address" input) > pickupLocationName (pincode search from "Location Name" input) */}
                    <p className="text-base font-semibold text-gray-900">{booking.pickupLocation || booking.pickupLocationName}</p>
                    {booking.pickupLandmark && (
                      <p className="text-sm text-gray-600 mt-1">📍 {booking.pickupLandmark}</p>
                    )}
                    {booking.pickupPincode && (
                      <p className="text-sm text-gray-600">PIN: {booking.pickupPincode}</p>
                    )}
                  </div>
                </div>
              )}

              {booking.dropoffLocation && (
                <div className="flex items-start space-x-3">
                  <svg className="w-5 h-5 text-gray-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V7m0 0L9 4" />
                  </svg>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-500">Dropoff Location</p>
                    <p className="text-base font-semibold text-gray-900">{booking.dropoffLocation}</p>
                    {booking.dropoffLandmark && (
                      <p className="text-sm text-gray-600 mt-1">📍 {booking.dropoffLandmark}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Driver Information */}
              {booking.driverName && (
                <div className="flex items-start space-x-3">
                  <svg className="w-5 h-5 text-gray-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-500">Driver</p>
                    <p className="text-base font-semibold text-gray-900">{booking.driverName}</p>
                    {booking.vehicleType && (
                      <p className="text-sm text-gray-600 mt-1">🚗 {booking.vehicleType}</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mt-6 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReserveReminderModal;







